<template>
    <flex-container class="order-detail-header">
        <button-outline-icon
            v-if="false"
            :icon="hasNotifications ? IconVisibility : IconVisibilityOff"
            :color="hasNotifications ? Color.brandGreen : Color.gray400"
        >
            Houdt mij op de hoogte van fase en status wijzigingen bij deze opdracht
        </button-outline-icon>

        <div>
            <div class="order-detail-header__actions">
                <collapse-box-decorator
                    trigger="click"
                    horizontal-position="right"
                >
                    <template #default="{ bindings }">
                        <button-default
                            v-bind="bindings"
                        >
                            <svg-icon
                                :icon="IconManageAccounts"
                                :color="Color.brandGreen"
                            />
                        </button-default>
                    </template>

                    <template #title>
                        Acties
                    </template>

                    <template #content>
                        <collapse-option-container>
                            <collapse-option @click="confirmOrderDelete">
                                <text-decorator :color="Color.red500">
                                    Opdracht verwijderen
                                </text-decorator>
                            </collapse-option>
                        </collapse-option-container>
                    </template>
                </collapse-box-decorator>

                <confirmation-dialog
                    ref="deleteConfirmDialog"
                    label="Weet u zeker dat u deze opdracht wilt verwijderen?"
                    :description="
                        `U probeert de ${labelForType(props.order.type)} opdracht te verwijderen, dit kan niet teruggedraaid worden. Weet u dit zeker?`
                    "
                >
                    <template #default>
                        <text-heading>Weet u zeker dat u deze opdracht wilt verwijderen?</text-heading>
                        <p>
                            U probeert de {{ labelForType(props.order.type) }} opdracht te verwijderen, dit kan niet teruggedraaid worden.<br>
                            Weet u dit zeker?
                        </p>
                    </template>
                    <template #actions="{ bindings }">
                        <button-outline @click="bindings.overlay.closeOverlay();">
                            Annuleren
                        </button-outline>
                        <button-block @loading="deleteOrder">
                            Verwijderen
                        </button-block>
                    </template>
                </confirmation-dialog>
            </div>

            <flex-container class="order-detail-header__properties">
                <div class="order-detail-header__property">
                    <text-heading sub>
                        {{ labelForType(props.order.type) }}
                    </text-heading>
                </div>
                <div class="order-detail-header__property">
                    &bull;
                </div>
                <div class="order-detail-header__property">
                    {{ labelForStatus(props.order.status) }}
                </div>
            </flex-container>
        </div>
    </flex-container>
</template>

<script setup lang="ts">
    import {
        Color,
        FlexContainer,
        TextHeading,
        ButtonOutlineIcon,
        ButtonOutline,
        CollapseOptionContainer,
        CollapseOption,
        ButtonDefault,
        SvgIcon,
        CollapseBoxDecorator,
        ConfirmationDialog,
        type ExposedOverlay,
        type ButtonStateChangedEvent,
        ButtonState,
    } from '@keyvue/one-ui';

    import IconVisibility from '@keyvue/one-material-icons/icons/visibility.svg?component';
    import IconVisibilityOff from '@keyvue/one-material-icons/icons/visibility_off.svg?component';
    import IconManageAccounts from '@keyvue/one-material-icons/icons/manage_accounts.svg?component';

    import type { Order } from '@keyvue/one-api-client-legacy';
    import { ref, computed, inject } from 'vue';
    import { OrderAPI } from '@keyvue/one-api-client-legacy';
    import useNotify from '@composables/useNotify';
    import useEventBus from '@composables/useEventBus';

    export interface Props {
        order: Order
    }

    const props = defineProps<Props>();

    const hasNotifications = computed(() => false);
    const deleteConfirmDialog = ref<ExposedOverlay>();
    const { notifySaving, notifyDeleted, notifyError } = useNotify();
    const eventBus = useEventBus();
    const closeOrder = inject<() => void>('closeOrder');

    const labelForType = (type: string): string => {
        switch (type) {
        case 'purchase':
            return 'Aankoop';
        case 'sale':
            return 'Verkoop';
        case 'mortgage':
            return 'Hypotheek';
        case 'rental':
            return 'Verhuur';
        default:
            return 'Onbekend type';
        }
    };

    const labelForStatus = (status: string): string => {
        switch (status) {
        case 'pending':
            return 'In behandeling';
        case 'successful':
            return 'Succesvol afgerond';
        case 'unsuccessful':
            return 'Niet succesvol afgerond';
        default:
            return 'Onbekende status';
        }
    };

    const confirmOrderDelete = () => {
        deleteConfirmDialog.value?.openOverlay();
    };

    const deleteOrder = async (event: ButtonStateChangedEvent) => {
        const closeNotification = notifySaving(undefined, 0);
        try {
            await OrderAPI.delete(props.order.id);
            eventBus.dispatch('resource-deleted', 'Order');

            notifyDeleted('De opdracht is verwijderd');
            event.button.setState(ButtonState.completed);
            deleteConfirmDialog.value?.closeOverlay();
            if (closeOrder) {
                closeOrder();
            }
        } catch (error) {
            notifyError('De opdracht kon niet verwijderd worden');
            event.button.setState(ButtonState.content);
        } finally {
            if (closeNotification) {
                closeNotification();
            }
        }
    };
</script>

<style scoped lang="scss">
.order-detail-header {
    padding: size(md);
    border-bottom: 1px solid color(gray-300);
    align-items: center;
    align-content: center;

    &__security,
    &__actions {
        position: absolute;
        top: size(xs);
        right: size(xs);
    }

    &__security {
        /** padding + actions button + padding + close button */
        right: calc(size(md) + size(md) + size(md) + size(lg));
    }

    &__actions {
        /** padding + close button */
        right: calc(size(md) + size(lg));
    }

    h2, h3 {
        margin-bottom: size(xs);
    }

    &__properties {
        .flex-container & {
            gap: size(sm);
        }

        font-size: font-size(xs);
    }

    &__property {
        display: flex;
        align-items: center;
        align-content: center;
        gap: size(sm);
    }
}
</style>
